import { useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';
import { Pause, Play } from 'lucide-react';
import * as THREE from 'three';
import { createHousePreviewTour, updateHousePreviewTour } from '../utils/measureUtils';

type Tuple = [number, number, number];
interface TourViewer {
  camera?: THREE.Camera;
  renderer?: { domElement?: HTMLCanvasElement };
  setScriptedCameraActive?: (active: boolean) => void;
  nextCamera?: { copy: (camera: THREE.Camera) => void };
  previousCamera?: { copy: (camera: THREE.Camera) => void };
}
const TOUR_DURATION_MS = 32_000;

export default function MedicalLakeTileTour({ viewerRef, initialTarget }: {
  viewerRef: MutableRefObject<TourViewer | null>; initialTarget: Tuple;
}) {
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(false);
  const completedRef = useRef(false);
  const elapsedRef = useRef(0);
  useEffect(() => {
    const viewer = viewerRef.current;
    const camera = viewer?.camera;
    const canvas = viewer?.renderer?.domElement;
    if (!viewer || !camera || !canvas) return;
    elapsedRef.current = 0;
    pausedRef.current = false;
    completedRef.current = false;
    setPaused(false);
    viewer.setScriptedCameraActive?.(true);
    const tour = createHousePreviewTour(camera.position.toArray() as Tuple, initialTarget);
    let lastTime: number | null = null;
    let frame = 0;
    let inView = true;
    const observer = new IntersectionObserver(([entry]) => { inView = entry.isIntersecting; });
    observer.observe(canvas);
    const animate = (now: number) => {
      const delta = lastTime == null ? 0 : Math.min(now - lastTime, 100);
      lastTime = now;
      if (!document.hidden && inView && !pausedRef.current) {
        elapsedRef.current += delta;
        const progress = Math.min(elapsedRef.current / TOUR_DURATION_MS, 1);
        updateHousePreviewTour(camera, tour, progress);
        viewer.nextCamera?.copy(camera);
        viewer.previousCamera?.copy(camera);
        if (progress === 1) {
          completedRef.current = true;
          pausedRef.current = true;
          setPaused(true);
        }
      }
      frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      viewer.setScriptedCameraActive?.(false);
    };
  }, [viewerRef, initialTarget]);

  const togglePlayback = () => {
    if (completedRef.current) {
      elapsedRef.current = 0;
      completedRef.current = false;
    }
    pausedRef.current = !pausedRef.current;
    setPaused(pausedRef.current);
  };

  const explore = () => {
    if (window.parent !== window) {
      window.parent.postMessage({ type: 'cr3ate:explore-realestate' }, window.location.origin);
    } else {
      window.location.assign('/realestate-viewer');
    }
  };
  return (
    <>
    <button type="button" onClick={togglePlayback} aria-label={paused ? 'Resume preview' : 'Pause preview'} aria-pressed={paused}
      className="group fixed inset-0 z-[1050] cursor-pointer bg-transparent focus-visible:outline focus-visible:outline-2 focus-visible:outline-white/60">
      <span className={`absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full border border-white/20 bg-black/45 text-white/90 backdrop-blur-sm transition-opacity ${paused ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100'}`}>
        {paused ? <Play size={13} /> : <Pause size={13} />}
      </span>
    </button>
    <div className="fixed inset-x-0 bottom-4 z-[1100] flex justify-center">
      <button type="button" onClick={explore}
        className="group flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-slate-800 to-slate-700 px-5 py-2.5 text-xs font-semibold text-white ring-1 ring-slate-500/60 transition-all hover:from-slate-700 hover:to-slate-600 hover:ring-slate-400/80 hover:shadow-md">
        <svg viewBox="0 0 20 20" className="h-4 w-4 text-slate-300 transition-colors group-hover:text-white" fill="none" aria-hidden="true">
          <path d="M10 2L2 7v6l8 5 8-5V7L10 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
          <path d="M2 7l8 5 8-5M10 12v7" stroke="currentColor" strokeWidth="1.5" />
        </svg>
        Explore in 3D
      </button>
    </div>
    </>
  );
}
